import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, PhoneNumber } from '../../types';

// ============================================
// Password Reset Step Type
// ============================================
export type PasswordResetStep = 0 | 1 | 2; // 0: email, 1: code, 2: new password

// ============================================
// Extended Auth State
// ============================================
interface ExtendedAuthState extends AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  passwordReset: {
    step: PasswordResetStep;
    email: string;
    token: string;
  };
}

// ============================================
// Initial State
// ============================================
const initialState: ExtendedAuthState = {
  phoneNumber: {
    countryCode: 'DE',
    callingCode: '49',
    number: '',
  },
  accessToken: localStorage.getItem('accessToken') || '',
  refreshToken: localStorage.getItem('refreshToken') || '',
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isLoading: false,
  error: null,
  passwordReset: {
    step: 0,
    email: '',
    token: '',
  },
};

// ============================================
// Auth Slice
// ============================================
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Phone number management
    setPhoneNumber: (state, action: PayloadAction<PhoneNumber>) => {
      state.phoneNumber = action.payload;
    },
    updatePhoneNumberValue: (state, action: PayloadAction<string>) => {
      state.phoneNumber.number = action.payload;
    },
    updateCountryCode: (
      state,
      action: PayloadAction<{ countryCode: string; callingCode: string }>
    ) => {
      state.phoneNumber.countryCode = action.payload.countryCode;
      state.phoneNumber.callingCode = action.payload.callingCode;
    },
    resetPhoneNumber: (state) => {
      state.phoneNumber = initialState.phoneNumber;
    },

    // Token management
    setTokens: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.error = null;
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
    clearTokens: (state) => {
      state.accessToken = '';
      state.refreshToken = '';
      state.isAuthenticated = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    },

    // Loading state
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Error handling
    setAuthError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Password reset flow
    setPasswordResetStep: (state, action: PayloadAction<PasswordResetStep>) => {
      state.passwordReset.step = action.payload;
    },
    setPasswordResetEmail: (state, action: PayloadAction<string>) => {
      state.passwordReset.email = action.payload;
    },
    setPasswordResetToken: (state, action: PayloadAction<string>) => {
      state.passwordReset.token = action.payload;
    },
    resetPasswordResetFlow: (state) => {
      state.passwordReset = initialState.passwordReset;
    },

    // Full reset
    resetAuth: () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return {
        ...initialState,
        accessToken: '',
        refreshToken: '',
        isAuthenticated: false,
      };
    },
  },
});

export const {
  setPhoneNumber,
  updatePhoneNumberValue,
  updateCountryCode,
  resetPhoneNumber,
  setTokens,
  clearTokens,
  setAuthLoading,
  setAuthError,
  setPasswordResetStep,
  setPasswordResetEmail,
  setPasswordResetToken,
  resetPasswordResetFlow,
  resetAuth,
} = authSlice.actions;

export default authSlice.reducer;
